import React, { useState } from 'react';
import { 
  FileText, Download, Shield, Scale, Lock, ClipboardCheck, 
  ExternalLink, CheckCircle, Building, Users, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const ComplianceDocuments = () => {
  const [activeTab, setActiveTab] = useState('documents');

  const documents = [
    {
      id: 'dpa',
      title: 'Data Processing Agreement (DPA)',
      description: 'Legal agreement governing how we process your data. Required for GDPR compliance.',
      icon: Scale,
      filename: 'NexusCMS_Data_Processing_Agreement.pdf',
      size: '~45 KB',
      lastUpdated: 'January 2025',
      audience: ['Schools', 'Businesses', 'Organizations']
    },
    {
      id: 'security-policy',
      title: 'Security Policy',
      description: 'Technical and organizational security measures we implement to protect your data.',
      icon: Shield,
      filename: 'NexusCMS_Security_Policy.pdf',
      size: '~25 KB',
      lastUpdated: 'January 2025',
      audience: ['IT Teams', 'Security Officers', 'Procurement']
    },
    {
      id: 'questionnaire',
      title: 'School Security Questionnaire',
      description: 'Pre-filled security questionnaire with answers for school procurement processes.',
      icon: ClipboardCheck,
      filename: 'NexusCMS_Security_Questionnaire.pdf',
      size: '~35 KB',
      lastUpdated: 'January 2025',
      audience: ['Schools', 'Education Authorities']
    },
    {
      id: 'privacy-template',
      title: 'Privacy Policy Template',
      description: 'Customizable privacy policy template for your website.',
      icon: Lock,
      filename: 'Privacy_Policy_Template.docx',
      size: '~15 KB',
      lastUpdated: 'January 2025',
      audience: ['All Customers']
    }
  ];

  const complianceStatus = [
    { standard: 'GDPR (EU)', status: 'compliant', notes: 'Full compliance with EU data protection regulations' },
    { standard: 'UK GDPR', status: 'compliant', notes: 'Compliant with UK data protection framework' },
    { standard: 'Australian Privacy Act', status: 'compliant', notes: 'Meets Australian Privacy Principles' },
    { standard: 'COPPA', status: 'compliant', notes: 'Children\'s privacy protections in place' },
    { standard: 'ISO 27001', status: 'planned', notes: 'Infrastructure providers certified; platform certification planned' },
    { standard: 'SOC 2 Type II', status: 'planned', notes: 'Infrastructure providers certified; platform certification planned' },
    { standard: 'Cyber Essentials (UK)', status: 'planned', notes: 'Certification planned for Q2 2025' }
  ];

  const securityFeatures = [
    { feature: 'Data Encryption (Transit)', status: true, details: 'TLS 1.2/1.3' },
    { feature: 'Data Encryption (Rest)', status: true, details: 'AES-256' },
    { feature: 'Two-Factor Authentication', status: true, details: 'TOTP-based' },
    { feature: 'Role-Based Access Control', status: true, details: '4 role levels' },
    { feature: 'Audit Logging', status: true, details: '12 month retention' },
    { feature: 'DDoS Protection', status: true, details: 'Cloudflare' },
    { feature: 'Web Application Firewall', status: true, details: 'Cloudflare WAF' },
    { feature: 'Automated Backups', status: true, details: 'Daily + hourly incremental' },
    { feature: 'Data Residency Options', status: true, details: 'EU, AU, Singapore' },
    { feature: 'Penetration Testing', status: true, details: 'Annual third-party testing' }
  ];

  const handleDownload = (doc) => {
    // In a real implementation, this would download the actual file
    // For now, we'll show a toast and provide instructions
    toast.success(`Preparing ${doc.title} for download...`);
    
    // Create a simple text version for demo
    const content = `
${doc.title}
==================

This document is available for download.
Please contact us at compliance@dynatexsaas.com to request the full document.

Document: ${doc.filename}
Last Updated: ${doc.lastUpdated}
    `.trim();
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.filename.replace('.pdf', '.txt').replace('.docx', '.txt');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleRequestDocument = (doc) => {
    window.location.href = `mailto:compliance@dynatexsaas.com?subject=Document Request: ${doc.title}&body=Hello,%0D%0A%0D%0AI would like to request a copy of the ${doc.title}.%0D%0A%0D%0AOrganization: %0D%0AContact Name: %0D%0A%0D%0AThank you.`;
  };

  return (
    <div className="flex">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Scale className="text-indigo-600" size={32} />
              Compliance & Documentation
            </h1>
            <p className="text-slate-500 mt-2">
              Security policies, compliance documents, and certifications for NexusCMS
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Shield className="text-emerald-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">AES-256</p>
                  <p className="text-xs text-slate-500">Encryption</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Lock className="text-indigo-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">2FA</p>
                  <p className="text-xs text-slate-500">Authentication</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Building className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">3</p>
                  <p className="text-xs text-slate-500">Data Regions</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">GDPR</p>
                  <p className="text-xs text-slate-500">Compliant</p>
                </div>
              </div>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="documents" className="gap-2">
                <FileText size={16} />
                Documents
              </TabsTrigger>
              <TabsTrigger value="compliance" className="gap-2">
                <CheckCircle size={16} />
                Compliance Status
              </TabsTrigger>
              <TabsTrigger value="security" className="gap-2">
                <Shield size={16} />
                Security Features
              </TabsTrigger>
            </TabsList>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {documents.map((doc) => {
                  const IconComponent = doc.icon;
                  return (
                    <div key={doc.id} className="bg-white rounded-xl border border-slate-200 p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="text-indigo-600" size={24} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{doc.title}</h3>
                          <p className="text-sm text-slate-500 mt-1">{doc.description}</p>
                          
                          <div className="flex flex-wrap gap-2 mt-3">
                            {doc.audience.map((aud, i) => (
                              <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                {aud}
                              </span>
                            ))}
                          </div>
                          
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <div className="text-xs text-slate-500 mb-4">
                              <p className="truncate">{doc.filename}</p>
                              <p>Updated: {doc.lastUpdated}</p>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Button size="sm" variant="outline" onClick={() => handleDownload(doc)}>
                                <Download size={14} className="mr-1" />
                                Preview
                              </Button>
                              <Button size="sm" onClick={() => handleRequestDocument(doc)}>
                                Request
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Request Custom Documents */}
              <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-6 mt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="text-indigo-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-indigo-900">Need a Custom Document?</h3>
                    <p className="text-sm text-indigo-700 mt-1">
                      If your organization requires additional documentation, security attestations, or custom agreements, please contact our compliance team.
                    </p>
                    <Button className="mt-4" onClick={() => window.location.href = 'mailto:compliance@dynatexsaas.com'}>
                      Contact Compliance Team
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Compliance Status Tab */}
            <TabsContent value="compliance">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold">Compliance & Certification Status</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Standard</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {complianceStatus.map((item, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium">{item.standard}</td>
                        <td className="px-6 py-4">
                          {item.status === 'compliant' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                              <CheckCircle size={12} />
                              Compliant
                            </span>
                          ) : item.status === 'planned' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                              <AlertTriangle size={12} />
                              Planned
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">
                              {item.status}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{item.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Data Residency */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6">
                <h3 className="font-semibold mb-4">Data Residency Options</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="border border-slate-200 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-1">🇪🇺</p>
                    <p className="font-medium">European Union</p>
                    <p className="text-sm text-slate-500">Frankfurt, Germany</p>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-1">🇦🇺</p>
                    <p className="font-medium">Australia</p>
                    <p className="text-sm text-slate-500">Sydney</p>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-1">🇸🇬</p>
                    <p className="font-medium">Southeast Asia</p>
                    <p className="text-sm text-slate-500">Singapore</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Security Features Tab */}
            <TabsContent value="security">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold">Security Features & Controls</h3>
                </div>
                <div className="divide-y divide-slate-200">
                  {securityFeatures.map((item, index) => (
                    <div key={index} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          item.status ? 'bg-emerald-100' : 'bg-slate-100'
                        }`}>
                          {item.status ? (
                            <CheckCircle className="text-emerald-600" size={16} />
                          ) : (
                            <AlertTriangle className="text-slate-400" size={16} />
                          )}
                        </div>
                        <span className="font-medium">{item.feature}</span>
                      </div>
                      <span className="text-sm text-slate-500">{item.details}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sub-processors */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6">
                <h3 className="font-semibold mb-4">Sub-processors</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">MongoDB Atlas</p>
                      <p className="text-sm text-slate-500">Database Hosting</p>
                    </div>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">SOC 2, ISO 27001</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Cloudflare</p>
                      <p className="text-sm text-slate-500">CDN, Security, WAF</p>
                    </div>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">SOC 2, ISO 27001</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">SendGrid (Twilio)</p>
                      <p className="text-sm text-slate-500">Email Delivery</p>
                    </div>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">SOC 2</span>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ComplianceDocuments;
